// ============================================
// FILE: src/pages/admin/Suppliers.jsx
// ============================================
import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Navbar from '../../components/Navbar';

const AdminSuppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSuppliers, setTotalSuppliers] = useState(0);
  const itemsPerPage = 20;

  // Use local backend if on localhost for verification
  const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8000/api' : process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchSuppliers();
  }, [currentPage]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await fetch(`${API_URL}/admin/suppliers?limit=${itemsPerPage}&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Suppliers API response:', data);
        setSuppliers(data.suppliers || []);
        setTotalSuppliers(data.total || 0);
      } else {
        console.error('API Error:', response.status, response.statusText);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        setSuppliers([]);
        setTotalSuppliers(0);
      }
    } catch (error) {
      console.error('Suppliers fetch error:', error);
      setSuppliers([]);
      toast.error('Network error - check backend connection');
    }
    setLoading(false);
  };

  const toggleVerification = async (supplierId, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/admin/suppliers/${supplierId}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isVerified: !currentStatus })
      });

      if (response.ok) {
        toast.success('Supplier verification updated');
        fetchSuppliers();
      } else {
        toast.error('Failed to update verification status');
      }
    } catch (error) {
      toast.error('Error updating verification status');
    }
  };

  const toggleActive = async (supplierId, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/admin/suppliers/${supplierId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (response.ok) {
        toast.success(`Supplier ${currentStatus ? 'deactivated' : 'activated'}`);
        fetchSuppliers();
      } else {
        toast.error('Failed to update supplier status');
      }
    } catch (error) {
      toast.error('Error updating supplier status');
    }
  };

  const deleteSupplier = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Supplier deleted successfully');
        fetchSuppliers();
      } else {
        const data = await response.json();
        toast.error(data.message || 'Failed to delete supplier');
      }
    } catch (error) {
      toast.error('Error deleting supplier');
    }
  };

  const viewSupplierDetails = (supplier) => {
    setSelectedSupplier(supplier);
    setShowDetails(true);
  };

  const closeSupplierDetails = () => {
    setSelectedSupplier(null);
    setShowDetails(false);
  };

  const totalPages = Math.ceil(totalSuppliers / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="min-h-screen bg-cover bg-center bg-fixed" style={{ backgroundImage: "url('/images/trade-bg.jpg')" }}>
      <div className="min-h-screen bg-black/40 w-full pb-10">
        <Navbar userType="admin" />

        {/* Supplier Details Modal */}
        {showDetails && selectedSupplier && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center z-10 w-full">
                <h2 className="text-2xl font-bold text-gray-900">Supplier Details: {selectedSupplier.business_name}</h2>
                <button onClick={closeSupplierDetails} className="text-gray-400 hover:text-gray-600 text-3xl font-light">×</button>
              </div>

              <div className="px-6 py-6 space-y-8">
                {/* 1. Identity & Contact */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">1. Identity & Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                    <div className="space-y-1">
                      <p><strong className="text-gray-600">Full Company Name:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.full_company_name || 'N/A'}</span></p>
                      <p><strong className="text-gray-600">Brand Name:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.brand_name || 'N/A'}</span></p>
                      <p><strong className="text-gray-600">Year Est:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.year_of_establishment || 'N/A'}</span></p>
                    </div>
                    <div className="space-y-1">
                      <p><strong className="text-gray-600">Entity Type:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.legal_entity_type || selectedSupplier.entity_type}{selectedSupplier.entity_type_other ? ` (${selectedSupplier.entity_type_other})` : ''}</span></p>
                      <p><strong className="text-gray-600">Business Type:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.business_model || selectedSupplier.business_type || 'N/A'}</span></p>
                      <p><strong className="text-gray-600">Business Nature:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.business_nature}{selectedSupplier.business_nature_other ? ` (${selectedSupplier.business_nature_other})` : ''}</span></p>
                    </div>
                    <div className="space-y-1">
                      <p><strong className="text-gray-600">GST Number:</strong> <span className="font-medium text-gray-900 block">{selectedSupplier.gst_number || 'N/A'}</span></p>
                      <p><strong className="text-gray-600">Website:</strong> <span className="truncate block"><a href={selectedSupplier.company_website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedSupplier.company_website_url || 'N/A'}</a></span></p>
                    </div>
                  </div>
                </div>

                {/* 2. Key Personnel */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">2. Key Personnel</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Owner / Main Contact</p>
                      <p><strong className="text-gray-600">Name:</strong> {selectedSupplier.owner_name}</p>
                      <p><strong className="text-gray-600">Designation:</strong> {selectedSupplier.designation || 'N/A'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Direct Contact</p>
                      <p><strong className="text-gray-600">Mobile:</strong> {selectedSupplier.phone}</p>
                      <p><strong className="text-gray-600">Email:</strong> {selectedSupplier.email}</p>
                      <p><strong className="text-gray-600">WhatsApp:</strong> {selectedSupplier.whatsapp_number || 'N/A'}</p>
                    </div>
                    {selectedSupplier.contact_phone_with_code && (
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Alternative Contact</p>
                        <p><strong className="text-gray-600">Alt Phone:</strong> {selectedSupplier.alternate_contact_number || 'N/A'}</p>
                        <p><strong className="text-gray-600">Alt Contact:</strong> {selectedSupplier.contact_phone_with_code}</p>
                        <p><strong className="text-gray-600">Alt Email:</strong> {selectedSupplier.contact_email}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 3. Locations */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">3. Locations & Addresses</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-bold text-blue-900 mb-2">Registered Address</h4>
                      <p className="whitespace-pre-wrap text-gray-800">{selectedSupplier.business_address}</p>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-gray-700">
                        <p><strong>Town/Area:</strong> {selectedSupplier.area_town || 'N/A'}</p>
                        <p><strong>City:</strong> {selectedSupplier.city}</p>
                        <p><strong>State:</strong> {selectedSupplier.state}</p>
                        <p><strong>Pincode:</strong> {selectedSupplier.pincode}</p>
                        <p><strong>Country:</strong> {selectedSupplier.head_office_country || 'N/A'}</p>
                      </div>
                      {selectedSupplier.google_map_location && (
                        <a href={selectedSupplier.google_map_location} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-blue-600 hover:text-blue-800 text-xs font-semibold">View on Google Maps →</a>
                      )}
                    </div>
                    {selectedSupplier.factory_plant_address && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-bold text-green-900 mb-2">Factory / Plant Address</h4>
                        <p className="whitespace-pre-wrap text-gray-800">{selectedSupplier.factory_plant_address}</p>
                        <div className="mt-2 text-gray-700">
                          <p><strong>Country:</strong> {selectedSupplier.factory_plant_country}</p>
                          <p><strong>Postal Code:</strong> {selectedSupplier.factory_plant_postal_code}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. Business & Products */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">4. Business Activities</h3>
                  <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p><strong className="text-gray-600">Primary Category:</strong> <span className="text-gray-900 font-medium">{selectedSupplier.primary_business_category || 'N/A'}</span></p>
                        {selectedSupplier.secondary_categories && (
                          <div className="mt-2">
                            <strong className="text-gray-600">Secondary Categories:</strong>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(Array.isArray(selectedSupplier.secondary_categories) ? selectedSupplier.secondary_categories : [])
                                .map((cat, i) => <span key={i} className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-700">{cat}</span>)}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <p><strong className="text-gray-600">Industry Served:</strong> <span className="text-gray-900">{selectedSupplier.industry_served || selectedSupplier.industry_sector || 'N/A'}</span></p>
                        <p><strong className="text-gray-600">Key Product:</strong> <span className="text-gray-900">{selectedSupplier.key_product_service || 'N/A'}</span></p>
                      </div>
                    </div>

                    <div>
                      <strong className="text-gray-600 block mb-1">Products / Services Offered:</strong>
                      <p className="bg-white p-3 border rounded text-gray-800 whitespace-pre-wrap">{selectedSupplier.products_services_offered || 'N/A'}</p>
                    </div>

                    {selectedSupplier.service_coverage_area && (
                      <div>
                        <strong className="text-gray-600 block mb-1">Service Coverage Area:</strong>
                        <div className="flex gap-4">
                          {Object.entries(selectedSupplier.service_coverage_area).map(([area, active]) => (
                            active && <span key={area} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">{area}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 5. Compliance & Legal */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">5. Legal & Compliance</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
                    {[
                      { l: 'GST Reg', v: selectedSupplier.gst_registered },
                      { l: 'MSME/UDYAM', v: selectedSupplier.msme_registered },
                      { l: 'FSSAI', v: selectedSupplier.fssai_registered },
                      { l: 'Trade License', v: selectedSupplier.trade_license_registered },
                      { l: 'IE Code', v: selectedSupplier.iec_registered },
                      { l: 'ISO Cert', v: selectedSupplier.iso_registered },
                      { l: 'Professional Cert', v: selectedSupplier.professional_certificate_registered },
                      { l: 'Association Member', v: selectedSupplier.is_association_member },
                    ].map((item, i) => (
                      <div key={i} className={`p-3 rounded border text-center ${item.v ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100 opacity-60'}`}>
                        <span className={`block font-bold mb-1 ${item.v ? 'text-green-700' : 'text-gray-500'}`}>{item.l}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.v ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                          {item.v ? 'YES' : 'NO'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 6. About */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">6. Business Profile</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <strong className="text-gray-600 block mb-1">Description:</strong>
                      <div className="bg-gray-50 p-3 rounded text-sm text-gray-800 h-full">{selectedSupplier.description || 'N/A'}</div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <strong className="text-gray-600 block mb-1">USP / Strengths:</strong>
                        <div className="bg-yellow-50 p-3 rounded text-sm text-gray-800 border-l-4 border-yellow-400">{selectedSupplier.usp_strengths || 'N/A'}</div>
                      </div>
                      <div>
                        <strong className="text-gray-600 block mb-1">Awards / Achievements:</strong>
                        <div className="bg-purple-50 p-3 rounded text-sm text-gray-800">{selectedSupplier.awards_achievements || 'N/A'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 7. Documents & Media */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4">7. Documents & Media</h3>

                  {/* Logo */}
                  {selectedSupplier.logo_url && (
                    <div className="mb-6 flex items-center space-x-4">
                      <strong className="text-gray-600">Company Logo:</strong>
                      <a href={selectedSupplier.logo_url} target="_blank" rel="noopener noreferrer" className="block w-24 h-24 border rounded overflow-hidden bg-white">
                        <img src={selectedSupplier.logo_url} className="w-full h-full object-contain" alt="Logo" />
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Certificates */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Certificates</h4>
                      <div className="space-y-4">
                        {selectedSupplier.certificate_files && selectedSupplier.certificate_files.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {selectedSupplier.certificate_files.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2 bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100 text-sm">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                Registration Cert {i + 1}
                              </a>
                            ))}
                          </div>
                        ) : <p className="text-sm text-gray-500 italic">No registration certificates</p>}

                        {selectedSupplier.trained_certificate_files && selectedSupplier.trained_certificate_files.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 mb-1">Professional / Trained Certs:</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedSupplier.trained_certificate_files.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center px-3 py-2 bg-indigo-50 text-indigo-700 rounded border border-indigo-200 hover:bg-indigo-100 text-sm">
                                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z"></path></svg>
                                  Prof Cert {i + 1}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Photos */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Product & Office Photos</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {selectedSupplier.product_photos && selectedSupplier.product_photos.length > 0 && selectedSupplier.product_photos.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="aspect-square bg-gray-100 rounded overflow-hidden border hover:opacity-75 relative group">
                            <img src={url} className="w-full h-full object-cover" />
                            <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-[10px] p-1 truncate text-center">Product</span>
                          </a>
                        ))}
                        {selectedSupplier.office_photos && selectedSupplier.office_photos.length > 0 && selectedSupplier.office_photos.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="aspect-square bg-gray-100 rounded overflow-hidden border hover:opacity-75 relative group">
                            <img src={url} className="w-full h-full object-cover" />
                            <span className="absolute bottom-0 left-0 right-0 bg-green-900 bg-opacity-50 text-white text-[10px] p-1 truncate text-center">Office</span>
                          </a>
                        ))}
                      </div>
                      {(!selectedSupplier.product_photos?.length && !selectedSupplier.office_photos?.length) && (
                        <p className="text-sm text-gray-500 italic">No photos uploaded</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* 8. Referral & Declaration */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Referral Code */}
                  <div className="bg-green-50 p-4 border-l-4 border-green-500 rounded-r-lg flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-green-800 uppercase mb-1">Referral Code</h3>
                      {selectedSupplier.referral_code ? (
                        <span className="inline-block px-3 py-1 bg-green-600 text-white text-lg font-mono font-bold rounded-lg tracking-widest">
                          {selectedSupplier.referral_code}
                        </span>
                      ) : (
                        <span className="text-sm text-green-700 italic">No referral code used</span>
                      )}
                    </div>
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>

                  {/* Declaration */}
                  <div className="bg-yellow-50 p-4 border-l-4 border-yellow-400 rounded-r-lg">
                    <h3 className="text-sm font-bold text-yellow-800 uppercase mb-2">Declaration</h3>
                    <div className="space-y-1 text-sm text-yellow-900">
                      <p>Accepted: <strong>{selectedSupplier.declaration_accepted ? 'YES' : 'NO'}</strong></p>
                      <p>Date: <strong>{selectedSupplier.declaration_date ? new Date(selectedSupplier.declaration_date).toLocaleDateString() : 'N/A'}</strong></p>
                      <p>Signed By: <strong>{selectedSupplier.applicant_signature || 'N/A'}</strong></p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
                <button onClick={closeSupplierDetails} className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">Close</button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Manage Suppliers</h1>

            {loading ? (
              <div className="text-center py-12">Loading suppliers...</div>
            ) : (
              <div className="bg-white shadow overflow-hidden sm:rounded-md flex flex-col min-h-[500px]">
                <div className="flex-1">
                  <ul className="divide-y divide-gray-200">
                    {suppliers.map((supplier) => (
                      <li key={supplier.id} className="px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center">
                              <div className="flex-1 min-w-0">
                                <p className="text-lg font-medium text-gray-900 truncate">
                                  {supplier.business_name}
                                </p>
                                <p className="text-sm text-gray-600 truncate">
                                  {supplier.owner_name} • {supplier.email}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Phone: {supplier.phone} • Rating: {supplier.rating || 'N/A'} • Orders: {supplier.total_orders}
                                  {supplier.referral_code && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-mono font-bold rounded bg-green-100 text-green-800 border border-green-300">
                                      🏷 {supplier.referral_code}
                                    </span>
                                  )}
                                </p>
                                <div className="flex items-center space-x-4 mt-2">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${supplier.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                    {supplier.is_verified ? 'Verified' : 'Pending Verification'}
                                  </span>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${supplier.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                    }`}>
                                    {supplier.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${supplier.subscription_status === 'active' ? 'bg-blue-100 text-blue-800' :
                                    supplier.subscription_status === 'expired' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                    Subscription: {supplier.subscription_status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="ml-4 flex-shrink-0 flex space-x-2">
                            {!supplier.is_verified && (
                              <button
                                onClick={() => toggleVerification(supplier.id, supplier.is_verified)}
                                className="text-green-600 hover:text-green-900 text-sm px-3 py-1 border border-green-600 rounded hover:bg-green-50"
                              >
                                Verify
                              </button>
                            )}
                            <button
                              onClick={() => toggleActive(supplier.id, supplier.is_active)}
                              className={`text-sm px-3 py-1 border rounded hover:bg-opacity-50 ${supplier.is_active
                                ? 'text-red-600 border-red-600 hover:bg-red-50'
                                : 'text-green-600 border-green-600 hover:bg-green-50'
                                }`}
                            >
                              {supplier.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => viewSupplierDetails(supplier)}
                              className="text-blue-600 hover:text-blue-900 text-sm px-3 py-1 border border-blue-600 rounded hover:bg-blue-50"
                            >
                              View Details
                            </button>
                            <button
                              onClick={() => deleteSupplier(supplier.id)}
                              className="text-red-700 hover:text-red-900 text-sm px-3 py-1 border border-red-700 rounded hover:bg-red-50 bg-red-50 hover:bg-red-100"
                              title="Delete Supplier"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pagination Controls */}
                {totalSuppliers > itemsPerPage && (
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalSuppliers)}</span> of <span className="font-medium">{totalSuppliers}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className="sr-only">Previous</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>

                          {/* Page Numbers */}
                          {[...Array(totalPages)].map((_, i) => (
                            <button
                              key={i}
                              onClick={() => handlePageChange(i + 1)}
                              aria-current={currentPage === i + 1 ? 'page' : undefined}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                              {i + 1}
                            </button>
                          ))}

                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className="sr-only">Next</span>
                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSuppliers;